import * as THREE from "three";
import { PLANT_LOOKS, T, TILES, inGrid, isCover, isMachineAnchor, isSolid, type BlockKey, type Grid, type Pt, type TileShape } from "@dyson/shared";
import { depthFade } from "./depthFade";
import { bendWithTouch, glowWithTouch } from "./plantTouch";
import { GLOW, TILE_PX, VARIANTS, atlasTexture, tileUv } from "./TileAtlas";

/** Non-solid tiles sit a little behind the play plane so characters draw in front of them. */
const BACK_Z = -0.35;
const BACK_SHADE = 0.6;
/** Water is drawn in front of everything as a tinted sheet, so ships and swimmers look submerged. */
const WATER_Z = 0.6;
/** How thick the turf along a grassy slope is, in tiles. */
const TURF = 0.22;
/** Turf grows on surface facing within this much (a dot with up) of the tile's up: not on undersides. */
const TURF_FACING = 0.3;
/** Which way grass turf faces for each quarter turn (see tileFacing): up, left, down, right. */
const TURF_DIRS = [
  [0, 1],
  [-1, 0],
  [0, -1],
  [1, 0],
] as const;
/** A square tile's outline, counter-clockwise. */
const SQUARE: Pt[] = [
  { x: 0, y: 0 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
];
/** How near a side of the tile an outline point must be to lie on it. */
const EDGE = 1e-6;
/** Rims (see GridMesh.rims): four texels wide (a pixel or two at the usual zoom), darker, or lighter on the edge facing up; just in front of the tile. */
const RIM = 4 / TILE_PX;
const RIM_DARK = 0.45;
const RIM_LIGHT = 1.4;
const RIM_Z = 0.004;

// Clear pixels in a tile (the corners of a turret's dome, round bulbs) show what's behind it.
export const tileMaterial = new THREE.MeshBasicMaterial({ map: atlasTexture, vertexColors: true, alphaTest: 0.5 });
bendWithTouch(tileMaterial);

/** Plants that bend away from whoever passes, and sway (see plantTouch). */
const SWAYS = ["REEDS", "SHRUB", "HANGING_VINE", "FROST_FLOWER", "GLOW_FUNGUS", "ROOTS", "THISTLE", "FERN", "FROST_GRASS", "CINDER_BLOOM", "LICHEN_FINGERS", "GLOW_DROPS"];
/** Their tiles in every look, and a living rock's foliage. */
const BENDS = new Set<number>([T.FOLIAGE, ...PLANT_LOOKS.flatMap((look) => SWAYS.map((kind) => T[`${look.toUpperCase()}_${kind}` as BlockKey]))]);

/**
 * Furnace fire, brighter than white so the bloom pass makes it glow. Shared
 * by every engine tile; the renderer flickers it.
 */
/** Unexplored, these still show what they are (a living skin's layers are no secret), but a sap pocket looks like the sapwood round it till it's found. */
const LAYERS = new Map<number, number>([
  [T.BARK, T.BARK],
  [T.SAPWOOD, T.SAPWOOD],
  [T.SAP, T.SAPWOOD],
  [T.HUSK, T.HUSK],
]);

/** The body of a rock: unexplored, it shows as itself (an ice rock is icy through, a big rock's core dark). */
const BODY = new Set<number>([T.ROCK, T.DEEP_ROCK, T.REGOLITH, T.ICE]);

/**
 * What an unexplored tile looks like: the rock's body as it is (and what
 * `host` says the rock is made of there, as it is: a nugget's coal or ore);
 * but what's hidden in it (ore, coal, crystal, a cave, a buried wreck) looks
 * like the body next to it, or failing that what `host` says belongs there,
 * so nothing's given away.
 */
function unexplored(grid: Grid, i: number, host: (x: number, y: number) => number): number {
  const t = grid.tiles[i]!;
  const layer = LAYERS.get(t);
  if (layer !== undefined) return layer;
  const x = i % grid.w;
  const y = (i - x) / grid.w;
  const here = host(x, y);
  if (BODY.has(t) || t === here) return t;
  for (const [dx, dy] of NEIGHBOURS) {
    if (!inGrid(grid, x + dx, y + dy)) continue;
    const n = grid.tiles[i + dy * grid.w + dx]!;
    if (BODY.has(n) || n === host(x + dx, y + dy)) return n;
  }
  return here;
}
const NEIGHBOURS = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]] as const;

export const engineGlowMaterial = new THREE.MeshBasicMaterial({ color: new THREE.Color(3.2, 1.3, 0.35), fog: false });
export const ENGINE_GLOW = new THREE.Color(3.2, 1.3, 0.35);

/**
 * Glowing blocks (see GLOW): per-tile HDR colours (vertex colours above 1,
 * so they bloom). The renderer pulses the material's colour.
 */
export const bioGlowMaterial = new THREE.MeshBasicMaterial({ vertexColors: true, fog: false });
glowWithTouch(bioGlowMaterial);

const waterMaterial = new THREE.MeshBasicMaterial({
  color: new THREE.Color(0.22, 0.5, 0.85),
  transparent: true,
  opacity: 0.5,
  depthWrite: false,
});

export interface GridRebuildOptions {
  /** Brightness for a tile; omit for full brightness. The suns' part: it dims with them (see setSun). */
  light?: (x: number, y: number) => number;
  /** Light a tile gets from lanterns, on top of `light`: it doesn't dim with the suns. */
  lamp?: (x: number, y: number) => number;
  /** Only build tiles in [x0, x1) x [y0, y1). Defaults to the whole grid. */
  region?: { x0: number; y0: number; x1: number; y1: number };
  /** 1 = visible (from visibleTiles). Tiles marked 0 are drawn as dark unexplored rock (see unexplored), even empty ones. */
  visible?: Uint8Array;
  /** What unexplored rock is at a tile where nothing round it says (see unexplored): what the rock's made of there. Deep stone if omitted. */
  host?: (x: number, y: number) => number;
  /** Tiles marked 1 in `mask` are drawn as `tile` instead: a ship's inside, hidden behind its hull. */
  cover?: { mask: Uint8Array; tile: number };
  /**
   * Which way a tile's texture faces, in quarter turns counter-clockwise
   * (0 = its top toward +Y). Omit to draw every tile upright.
   */
  orient?: (x: number, y: number, tile: number) => number;
  /**
   * How a tile is shaped (see TileShape): ground smoothed to its neighbours
   * (see groundShape), or a shaped block's own outline (see shapes.ts). Omit,
   * or return null, to draw every tile square.
   */
  shape?: (x: number, y: number, tile: number) => TileShape | null;
  /**
   * Light fades into the solid part, deeper being darker (see depthFade):
   * rock seen from a tunnel shades off into the dark, unexplored rock
   * included. For asteroids; a ship's hull stays evenly lit.
   */
  fade?: boolean;
}

/**
 * One mesh for a tile grid, or a region of one (ship, or a chunk of an
 * asteroid): a textured quad per non-empty tile, in grid-local coordinates
 * with tile (x, y) covering [x, x+1] x [y, y+1]. Rebuilt wholesale when a tile
 * changes.
 */
export class GridMesh {
  readonly mesh: THREE.Mesh;
  /** Glowing grates over furnace tiles, a child of `mesh`. */
  private readonly glow: THREE.Mesh;
  /** Translucent water over water tiles, a child of `mesh`. */
  private readonly water: THREE.Mesh;
  /** Glowing hearts of bulbs and crystals, a child of `mesh`. */
  private readonly bio: THREE.Mesh;
  private hasFire = false;
  private fireOn = true;
  /** Per vertex: the suns' shade and the lanterns' (null with none), and how bright the suns are now (see setSun). */
  private sunCol = new Float32Array(0);
  private lampCol: Float32Array | null = null;
  private sunK = 1;

  constructor(material: THREE.Material = tileMaterial) {
    this.mesh = new THREE.Mesh(new THREE.BufferGeometry(), material);
    this.glow = new THREE.Mesh(new THREE.BufferGeometry(), engineGlowMaterial);
    this.water = new THREE.Mesh(new THREE.BufferGeometry(), waterMaterial);
    this.water.renderOrder = 5;
    this.bio = new THREE.Mesh(new THREE.BufferGeometry(), bioGlowMaterial);
    this.mesh.add(this.glow, this.water, this.bio);
  }

  rebuild(grid: Grid, opts: GridRebuildOptions = {}): void {
    const { light, lamp, visible, orient, cover, shape } = opts;
    const host = opts.host ?? (() => T.DEEP_ROCK);
    const r = opts.region ?? { x0: 0, y0: 0, x1: grid.w, y1: grid.h };
    const pos: number[] = [];
    const uv: number[] = [];
    const col: number[] = [];
    /** Per vertex, the lanterns' light (see GridRebuildOptions.lamp). */
    const lampCol: number[] = [];
    /** Per vertex: 1 where the tile bends with touch (see BENDS). */
    const bend: number[] = [];
    const waterPos: number[] = [];
    const glowPos: number[] = [];
    const bioPos: number[] = [];
    const bioCol: number[] = [];

    /** The tile as drawn: a ship's inside seen from outside is hull, and unexplored rock is rock, but for the layers of a living skin (null: off the grid). */
    const drawn = (x: number, y: number): number | null => {
      if (!inGrid(grid, x, y)) return null;
      const i = y * grid.w + x;
      if (cover?.mask[i]) return cover.tile;
      if (visible && !visible[i]) return unexplored(grid, i, host);
      return grid.tiles[i]!;
    };
    const solidAt = (x: number, y: number) => {
      const t = drawn(x, y);
      return t !== null && isSolid(t);
    };
    const fade = opts.fade ? depthFade(grid, r, solidAt) : () => 1;
    // Each corner's shade (light, faded into rock), worked out once: corner (cx, cy) is at index (cy - y0) * (w + 1) + (cx - x0).
    const cw = r.x1 - r.x0 + 1;
    const cornerShade = new Float32Array(cw * (r.y1 - r.y0 + 1));
    const cornerLamp = lamp ? new Float32Array(cornerShade.length) : null;
    for (let cy = r.y0; cy <= r.y1; cy++) {
      for (let cx = r.x0; cx <= r.x1; cx++) {
        const f = fade(cx, cy);
        cornerShade[(cy - r.y0) * cw + cx - r.x0] = (light ? light(cx, cy) : 1) * f;
        if (cornerLamp) cornerLamp[(cy - r.y0) * cw + cx - r.x0] = lamp!(cx, cy) * f;
      }
    }

    for (let y = r.y0; y < r.y1; y++) {
      for (let x = r.x0; x < r.x1; x++) {
        const i = y * grid.w + x;
        const hidden = visible ? !visible[i] : false;
        const t = drawn(x, y)!;
        // The shade at a point of the tile (its [0, 1] square), blended from its corners.
        const c00 = cornerShade[(y - r.y0) * cw + x - r.x0]!;
        const c10 = cornerShade[(y - r.y0) * cw + x + 1 - r.x0]!;
        const c01 = cornerShade[(y + 1 - r.y0) * cw + x - r.x0]!;
        const c11 = cornerShade[(y + 1 - r.y0) * cw + x + 1 - r.x0]!;
        const shadeAt = (lx: number, ly: number, tone: number) => tone * (c00 * (1 - lx) * (1 - ly) + c10 * lx * (1 - ly) + c01 * (1 - lx) * ly + c11 * lx * ly);
        const l00 = cornerLamp?.[(y - r.y0) * cw + x - r.x0] ?? 0;
        const l10 = cornerLamp?.[(y - r.y0) * cw + x + 1 - r.x0] ?? 0;
        const l01 = cornerLamp?.[(y + 1 - r.y0) * cw + x - r.x0] ?? 0;
        const l11 = cornerLamp?.[(y + 1 - r.y0) * cw + x + 1 - r.x0] ?? 0;
        const lampAt = (lx: number, ly: number, tone: number) => tone * (l00 * (1 - lx) * (1 - ly) + l10 * lx * (1 - ly) + l01 * (1 - lx) * ly + l11 * lx * ly);
        /** A vertex's colour: the suns' shade and the lanterns', pushed side by side. */
        const shade = (lx: number, ly: number, tone: number) => {
          const s = shadeAt(lx, ly, tone);
          col.push(s, s, s);
          const l = cornerLamp ? lampAt(lx, ly, tone) : 0;
          lampCol.push(l, l, l);
        };
        /** How a tile's texture is laid over this tile's square: a corner of the atlas and how many quarter turns it's turned. */
        const paint = (px: number, py: number, pt: number) => {
          const q = orient ? orient(px, py, pt) & 3 : 0;
          const { u0, u1, v0, v1 } = tileUv(pt, Math.floor(hash01(px + 7919, py - 104729) * VARIANTS));
          const texel = (lx: number, ly: number) => {
            for (let k = 0; k < q; k++) [lx, ly] = [ly, 1 - lx];
            return [u0 + (u1 - u0) * lx, v0 + (v1 - v0) * ly] as const;
          };
          // Turf is the top rows of the (unturned) cover texture: grass, moss, frost, bark.
          const turf = [u0 + (u1 - u0) * 0.5, v0 + (v1 - v0) * (1 - 2.5 / TILE_PX)] as const;
          return { q, texel, turf };
        };
        const form = !hidden && shape ? shape(x, y, t) : null;
        /**
         * Draw pieces of this tile's square as tile `pt` (at (px, py): this one, or the ground next door whose slivers reach in),
         * with its rims along their exposed edges and its turf along the surface if it's ground cover.
         */
        const drawPieces = (pieces: readonly Pt[][], surface: readonly (readonly [Pt, Pt])[], px: number, py: number, pt: number, z: number, tone: number, rims: boolean) => {
          const { q, texel, turf } = paint(px, py, pt);
          const vertex = (lx: number, ly: number, dz = 0, tint = 1) => {
            pos.push(x + lx, y + ly, z + dz);
            uv.push(...texel(lx, ly));
            shade(lx, ly, tone * tint);
          };
          for (const piece of pieces) for (let k = 1; k + 1 < piece.length; k++) for (const p of [piece[0]!, piece[k]!, piece[k + 1]!]) vertex(p.x, p.y);
          if (rims) this.rims(pieces, surface, TURF_DIRS[q]!, (dx, dy) => !solidAt(x + dx, y + dy), vertex);
          if (isCover(pt)) this.turfAlong(x, y, z, TURF_DIRS[q]!, surface, turf, (lx, ly) => shade(lx, ly, tone), pos, uv);
        };
        // Slivers of the smooth ground next door reaching into an open tile (see TileShape.owners): drawn as that ground, in front.
        if (form?.owners) {
          const byOwner = new Map<string, Pt[][]>();
          form.pieces.forEach((piece, k) => {
            const o = form.owners![k] ?? [0, 0];
            const key = `${o[0]},${o[1]}`;
            byOwner.set(key, [...(byOwner.get(key) ?? []), piece]);
          });
          for (const [key, pieces] of byOwner) {
            const [dx, dy] = key.split(",").map(Number) as [number, number];
            const pt = drawn(x + dx, y + dy);
            if (pt === null || pt === T.EMPTY) continue;
            drawPieces(pieces, form.surface, x + dx, y + dy, pt, 0, 0.92 + 0.08 * hash01(x + dx, y + dy), true);
          }
        }
        // Trunks are drawn by their tree's sprite (see Trees).
        if (t === T.EMPTY || t === T.TREE) continue;
        if (t === T.WATER) {
          waterPos.push(x, y, WATER_Z, x + 1, y, WATER_Z, x + 1, y + 1, WATER_Z, x, y, WATER_Z, x + 1, y + 1, WATER_Z, x, y + 1, WATER_Z);
          continue;
        }
        const back = !isSolid(t);
        const z = back ? BACK_Z : 0;
        // Small per-tile tone jitter so big rock faces do not look stamped.
        const tone = (0.92 + 0.08 * hash01(x, y)) * (back ? BACK_SHADE : 1);
        // Smoothed ground and shaped blocks are drawn as their pieces, a fan each; anything else (an open tile too, behind) as a square.
        const own = form && !form.owners;
        drawPieces(own ? form.pieces : [SQUARE], own ? form.surface : [], x, y, t, z, tone, !back && !hidden);
        if (isMachineAnchor(t, "furnace")) {
          // At each furnace's anchor, one quad over its fire grate (matches the atlas art, 2x2 tiles).
          const [x0, x1, y0, y1] = [x + 0.5, x + 1.5, y + 7 / 16, y + 19 / 16];
          glowPos.push(x0, y0, 0.02, x1, y0, 0.02, x1, y1, 0.02, x0, y0, 0.02, x1, y1, 0.02, x0, y1, 0.02);
        }
        const bio = GLOW[TILES[t]!.key];
        if (bio) {
          const k = bio.inset;
          const bz = z + 0.02;
          const [x0, x1, y0, y1] = [x + k, x + 1 - k, y + k, y + 1 - k];
          bioPos.push(x0, y0, bz, x1, y0, bz, x1, y1, bz, x0, y0, bz, x1, y1, bz, x0, y1, bz);
          for (let v = 0; v < 6; v++) bioCol.push(...bio.color);
        }
        const bends = !hidden && BENDS.has(t) ? 1 : 0;
        while (bend.length < pos.length / 3) bend.push(bends);
      }
    }

    const g = this.mesh.geometry;
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(pos), 3));
    g.setAttribute("uv", new THREE.BufferAttribute(new Float32Array(uv), 2));
    this.sunCol = new Float32Array(col);
    this.lampCol = lamp ? new Float32Array(lampCol) : null;
    g.setAttribute("color", new THREE.BufferAttribute(this.lit(), 3));
    g.setAttribute("aBend", new THREE.BufferAttribute(new Float32Array(bend), 1));
    g.computeBoundingSphere();
    setPositions(this.glow, glowPos);
    this.hasFire = glowPos.length > 0;
    this.glow.visible = this.hasFire && this.fireOn;
    setPositions(this.water, waterPos);
    this.bio.geometry.setAttribute("color", new THREE.BufferAttribute(new Float32Array(bioCol), 3));
    setPositions(this.bio, bioPos);
  }

  /**
   * A solid tile's rims: along each edge of its pieces that faces open space
   * (a side of the square with nothing solid beyond it, or the surface
   * through the tile, see TileShape), a strip RIM wide just inside the edge,
   * the tile's own texture darker, or lighter on the edge facing `up` (out
   * from an asteroid, or up a ship). Edges inside the tile (between its
   * pieces) get none. Ground and hulls read as solid shapes.
   */
  private rims(
    pieces: readonly Pt[][],
    surface: readonly (readonly [Pt, Pt])[],
    up: readonly [number, number],
    openTo: (dx: number, dy: number) => boolean,
    vertex: (lx: number, ly: number, dz: number, tint: number) => void,
  ): void {
    const near = (p: Pt, q: Pt) => Math.abs(p.x - q.x) < 1e-5 && Math.abs(p.y - q.y) < 1e-5;
    const isSurface = (a: Pt, b: Pt) => surface.some(([p, q]) => (near(a, p) && near(b, q)) || (near(a, q) && near(b, p)));
    for (const piece of pieces) {
      const n = piece.length;
      for (let k = 0; k < n; k++) {
        const a = piece[k]!;
        const b = piece[(k + 1) % n]!;
        // On a side of the square: exposed if there's nothing solid that way. Otherwise only the surface is.
        const exposed =
          a.x < EDGE && b.x < EDGE ? openTo(-1, 0)
          : a.x > 1 - EDGE && b.x > 1 - EDGE ? openTo(1, 0)
          : a.y < EDGE && b.y < EDGE ? openTo(0, -1)
          : a.y > 1 - EDGE && b.y > 1 - EDGE ? openTo(0, 1)
          : isSurface(a, b);
        if (!exposed) continue;
        // Pieces run counter-clockwise, so inward is to the left of a -> b.
        const len = Math.hypot(b.x - a.x, b.y - a.y);
        if (len < 1e-6) continue;
        const nx = -(b.y - a.y) / len;
        const ny = (b.x - a.x) / len;
        const tint = -nx * up[0] - ny * up[1] > 0.6 ? RIM_LIGHT : RIM_DARK;
        const [ia, ib] = [{ x: a.x + nx * RIM, y: a.y + ny * RIM }, { x: b.x + nx * RIM, y: b.y + ny * RIM }];
        for (const p of [a, b, ib, a, ib, ia]) vertex(p.x, p.y, RIM_Z, tint);
      }
    }
  }

  /**
   * Ground cover's turf follows the surface through the tile (see TileShape):
   * a strip of turf just inside each surface edge that faces up (within
   * TURF_FACING of `up`), so grass runs along a hillside and over a crest,
   * and not under an overhang.
   */
  private turfAlong(
    x: number,
    y: number,
    z: number,
    up: readonly [number, number],
    surface: readonly (readonly [Pt, Pt])[],
    turf: readonly [number, number],
    shade: (lx: number, ly: number) => void,
    pos: number[],
    uv: number[],
  ): void {
    for (const [a, b] of surface) {
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      if (len < 1e-6) continue;
      // Ground is on the left of a -> b: the strip lies that way, and the edge faces the other.
      const nx = -(b.y - a.y) / len;
      const ny = (b.x - a.x) / len;
      if (-nx * up[0] - ny * up[1] < TURF_FACING) continue;
      const an = { x: a.x + nx * TURF, y: a.y + ny * TURF };
      const bn = { x: b.x + nx * TURF, y: b.y + ny * TURF };
      for (const p of [a, b, bn, a, bn, an]) {
        pos.push(x + p.x, y + p.y, z + 0.01);
        uv.push(...turf);
        shade(p.x, p.y);
      }
    }
  }

  /** The vertex colours as lit now: the suns' shade times how bright they are, plus the lanterns'. */
  private lit(): Float32Array {
    const out = new Float32Array(this.sunCol.length);
    const k = this.sunK;
    const lamp = this.lampCol;
    for (let i = 0; i < out.length; i++) out[i] = this.sunCol[i]! * k + (lamp ? lamp[i]! : 0);
    return out;
  }

  /**
   * How bright the suns are here now, against how they were when it was
   * built (1): an outage dims the suns' part of the light baked in, but not
   * the lanterns' (see NIGHT). Cheap: only the colours are redone.
   */
  setSun(k: number): void {
    if (Math.abs(k - this.sunK) < 0.004) return;
    this.sunK = k;
    const attr = this.mesh.geometry.getAttribute("color") as THREE.BufferAttribute | undefined;
    if (!attr || attr.array.length !== this.sunCol.length) return;
    (attr.array as Float32Array).set(this.lit());
    attr.needsUpdate = true;
  }

  /** Furnace fire on or off (off when the ship is out of fuel). */
  setFire(on: boolean): void {
    this.fireOn = on;
    this.glow.visible = on && this.hasFire;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.glow.geometry.dispose();
    this.water.geometry.dispose();
    this.bio.geometry.dispose();
  }
}

function setPositions(mesh: THREE.Mesh, positions: number[]): void {
  mesh.geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(positions), 3));
  mesh.geometry.computeBoundingSphere();
  mesh.visible = positions.length > 0;
}

function hash01(x: number, y: number): number {
  let h = Math.imul(x, 374761393) ^ Math.imul(y, 668265263);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
